import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import ReactSlider from "react-slider";
import productService from "../services/productService";
import categoryService from "../services/categoryService";
import ProductCard from "./common/ProductCard";
import QuickViewModal from "./common/QuickViewModal";

const ShopSection = () => {
  let [grid, setGrid] = useState(false);
  // Dynamic data states
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null); // categoryId filter
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [errorProducts, setErrorProducts] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  // Additional filter states
  const [priceRange, setPriceRange] = useState([0, 0]);
  // Available max price for slider
  const [maxPriceAvailable, setMaxPriceAvailable] = useState(0);
  // Read search term from URL query
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchTerm = searchParams.get("searchTerm");
  // Read categoryId from URL query if present
  const categoryIdParam = searchParams.get("categoryId");
  
  // Quick View modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showQuickView, setShowQuickView] = useState(false);

  // If categoryId query param exists, set selectedCategory accordingly
  useEffect(() => {
    if (categoryIdParam) {
      setSelectedCategory(categoryIdParam);
      setPage(1);
    }
  }, [categoryIdParam]);

  // Fetch products based on current page and any applied filters
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      // Build params: if searching, use searchTerm; else use filters
      const params = { pageNumber: page, pageSize };
      if (searchTerm) {
        params.searchTerm = searchTerm;
      } else {
        if (selectedCategory !== null) params.categoryId = selectedCategory;
        if (priceRange[0] > 0 || priceRange[1] < maxPriceAvailable) {
          params.minPrice = priceRange[0];
          params.maxPrice = priceRange[1];
        }
      }
      const response = await productService.getProducts(params);
      if (response.succeeded) {
        setProducts(response.products);
        setTotalPages(response.totalPages);
        setTotalCount(response.total);
      } else {
        setErrorProducts(response.message);
      }
    } catch (err) {
      setErrorProducts(err.message);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Load main categories along with their product counts on mount
  useEffect(() => {
    const loadCategories = async () => {
      const catResp = await categoryService.getMainCategories();
      if (catResp.succeeded) {
        const cats = catResp.categories;
        const catsWithCount = await Promise.all(
          cats.map(async (cat) => {
            const resp = await productService.getProducts({
              categoryId: cat.id,
              pageNumber: 1,
              pageSize: 1,
            });
            const count = resp.succeeded ? resp.total : 0;
            return { ...cat, productCount: count };
          })
        );
        setCategories(catsWithCount);
      }
    };
    loadCategories();
  }, []);

  // When category changes or searchTerm changes, fetch accordingly
  useEffect(() => {
    const initCategory = async () => {
      setLoadingProducts(true);
      try {
        // If searching, fetch search results only
        if (searchTerm) {
          setPage(1);
          const resp = await productService.getProducts({
            searchTerm,
            pageNumber: 1,
            pageSize,
          });
          if (resp.succeeded) {
            setProducts(resp.products);
            setTotalPages(resp.totalPages);
            setTotalCount(resp.total);
          } else {
            setErrorProducts(resp.message);
          }
          return;
        }
        const priceResp = await productService.getMaxPrice(selectedCategory);
        let maxPrice = 0;
        if (priceResp.succeeded) {
          maxPrice = priceResp.maxPrice;
          setMaxPriceAvailable(maxPrice);
          setPriceRange([0, maxPrice]);
        }
        setPage(1);
        // Build query: unfiltered if no category selected, else include category and full price range
        const params = { pageNumber: 1, pageSize };
        if (selectedCategory !== null) {
          params.categoryId = selectedCategory;
          params.minPrice = 0;
          params.maxPrice = maxPrice;
        }
        const response = await productService.getProducts(params);
        if (response.succeeded) {
          setProducts(response.products);
          setTotalPages(response.totalPages);
          setTotalCount(response.total);
        } else {
          setErrorProducts(response.message);
        }
      } catch (err) {
        setErrorProducts(err.message);
      } finally {
        setLoadingProducts(false);
      }
    };
    initCategory();
  }, [selectedCategory, searchTerm]);

  // Fetch products when page or searchTerm changes
  useEffect(() => {
    fetchProducts();
  }, [page, searchTerm]);

  let [active, setActive] = useState(false);
  let sidebarController = () => {
    setActive(!active);
  };

  // Quick View handlers
  const handleQuickView = (product) => {
    setSelectedProduct(product);
    setShowQuickView(true);
  };

  const handleCloseQuickView = () => {
    setShowQuickView(false);
    setSelectedProduct(null);
  };

  // Compute display counts
  const startCount = (page - 1) * pageSize + 1;
  const endCount = Math.min(page * pageSize, totalCount);

  return (
    <section className="shop py-80">
      <div className={`side-overlay ${active && "show"}`}></div>
      <div className="container container-lg">
        <div className="row">
          {/* Sidebar Start */}
          <div className="col-lg-3">
            <div className={`shop-sidebar ${active && "active"}`}>
              <button
                onClick={sidebarController}
                type="button"
                className="shop-sidebar__close d-lg-none d-flex w-32 h-32 flex-center border border-gray-100 rounded-circle hover-bg-main-600 position-absolute inset-inline-end-0 me-10 mt-8 hover-text-white hover-border-main-600"
              >
                <i className="ph ph-x" />
              </button>
              <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                  Ürün Kategorileri
                </h6>
                <ul className="max-h-540 overflow-y-auto scroll-sm">
                  <li key="all" className="mb-24">
                    <button
                      type="button"
                      className={`text-gray-900 hover-text-main-600 ${
                        selectedCategory === null
                          ? "text-main-600 font-bold"
                          : ""
                      }`}
                      onClick={() => {
                        setSelectedCategory(null);
                        setPage(1);
                      }}
                    >
                      Tümü ({totalCount})
                    </button>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.id} className="mb-24">
                      <button
                        type="button"
                        className={`text-gray-900 hover-text-main-600 ${
                          selectedCategory === cat.id
                            ? "text-main-600 font-bold"
                            : ""
                        }`}
                        onClick={() => {
                          setSelectedCategory(cat.id);
                          setPage(1);
                        }}
                      >
                        {cat.name} ({cat.productCount})
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                  Fiyata Göre Filtrele
                </h6>
                <div className="custom--range">
                  <ReactSlider
                    className="horizontal-slider"
                    thumbClassName="example-thumb"
                    trackClassName="example-track"
                    min={0}
                    max={maxPriceAvailable}
                    value={priceRange}
                    onChange={(value) => setPriceRange(value)}
                    ariaLabel={["Lower thumb", "Upper thumb"]}
                    ariaValuetext={(state) => `Thumb value ${state.valueNow}`}
                    renderThumb={(props, state) => {
                      const { key, ...restProps } = props;
                      return (
                        <div {...restProps} key={state.index}>
                          {state.valueNow}
                        </div>
                      );
                    }}
                    pearling
                    minDistance={10}
                  />

                  <br />
                  <br />
                  <div className="flex-between flex-wrap-reverse gap-8 mt-24 ">
                    <button
                      type="button"
                      className="btn btn-main h-40 flex-align"
                      onClick={() => {
                        setPage(1);
                        fetchProducts();
                      }}
                    >
                      Filtrele
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Sidebar End */}
          {/* Content Start */}
          <div className="col-lg-9">
            {/* Top Start */}
            <div className="flex-between gap-16 flex-wrap mb-40 ">
              <span className="text-gray-900">
                Toplam {totalCount} ürün — {startCount}-{endCount} arası
                gösteriliyor
              </span>
              {/* Grid ve sıralama kontrollerini geçici olarak gizledim */}
              {/*
                            <div className="position-relative flex-align gap-16 flex-wrap">
                                <div className="list-grid-btns flex-align gap-16">
                                    <button onClick={() => setGrid(true)}
                                        type="button"
                                        className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${grid === true && "border-main-600 text-white bg-main-600"}`}
                                    >
                                        <i className="ph-bold ph-list-dashes" />
                                    </button>
                                    <button onClick={() => setGrid(false)}
                                        type="button"
                                        className={`w-44 h-44 flex-center border rounded-6 text-2xl grid-btn border-gray-100 ${grid === false && "border-main-600 text-white bg-main-600"}`}
                                    >
                                        <i className="ph ph-squares-four" />
                                    </button>
                                </div>
                                <div className="position-relative text-gray-500 flex-align gap-4 text-14">
                                    <label htmlFor="sorting" className="text-inherit flex-shrink-0">
                                        Sort by:{" "}
                                    </label>
                                    <select defaultValue={1}
                                        className="form-control common-input px-14 py-14 text-inherit rounded-6 w-auto"
                                        id="sorting"
                                    >
                                        <option value={1} >
                                            Popular
                                        </option>
                                        <option value={1}>Latest</option>
                                        <option value={1}>Trending</option>
                                        <option value={1}>Matches</option>
                                    </select>
                                </div>
                                <button onClick={sidebarController}
                                    type="button"
                                    className="w-44 h-44 d-lg-none d-flex flex-center border border-gray-100 rounded-6 text-2xl sidebar-btn"
                                >
                                    <i className="ph-bold ph-funnel" />
                                </button>
                            </div>
                            */}
              {/* Top End */}
            </div>
            {/* Top End */}
            <div className={`row gy-4 g-12 ${grid ? "list-view" : ""}`}>
              {" "}
              {/* Ürünler için Bootstrap grid */}
              {loadingProducts ? (
                <div className="container text-center py-40">
                  <div className="spinner-border text-main-600" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : errorProducts ? (
                <div className="alert alert-danger mb-16">{errorProducts}</div>
              ) : products.length > 0 ? (
                products.map((product) => (
                  <div
                    key={product.id}
                    className="col-xxl-3 col-lg-4 col-md-6 col-12"
                  >
                    <ProductCard 
                      product={product} 
                      viewMode="grid"
                      showQuickView={true}
                      compact={false}
                      onQuickView={handleQuickView}
                    />
                  </div>
                ))
              ) : (
                <p className="mt-16 text-center">
                  Bu kategoride ürün bulunamadı.
                </p>
              )}
            </div>
            {/* Pagination Start */}
            <ul className="pagination flex-center flex-wrap gap-16">
              <li className="page-item">
                <button
                  type="button"
                  disabled={page === 1}
                  className="page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium text-neutral-600 border border-gray-100"
                  onClick={() => setPage(page - 1)}
                >
                  <i className="ph-bold ph-arrow-left" />
                </button>
              </li>
              {Array.from({ length: totalPages }, (_, idx) => {
                const pageNum = idx + 1;
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${page === pageNum ? "active" : ""}`}
                  >
                    <button
                      type="button"
                      className={`page-link h-64 w-64 flex-center text-md rounded-8 fw-medium text-neutral-600 border border-gray-100 ${
                        page === pageNum
                          ? "border-main-600 text-white bg-main-600"
                          : ""
                      }`}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum < 10 ? `0${pageNum}` : pageNum}
                    </button>
                  </li>
                );
              })}
              <li className="page-item">
                <button
                  type="button"
                  disabled={page === totalPages}
                  className="page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium text-neutral-600 border border-gray-100"
                  onClick={() => setPage(page + 1)}
                >
                  <i className="ph-bold ph-arrow-right" />
                </button>
              </li>
            </ul>
            {/* Pagination End */}
          </div>
          {/* Content End */}
        </div>
      </div>

      {/* Quick View Modal */}
      <QuickViewModal
        product={selectedProduct}
        isOpen={showQuickView}
        onClose={handleCloseQuickView}
      />
    </section>
  );
};

export default ShopSection;
